import React from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import classnames from 'classnames';
import UserDatabases from '@Services/UserDatabases';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import get from 'lodash/get';
import CasNumberContainer, { isCasNumberAllowed } from '@Containers/CasNumber';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { TooltipConstants } from '@Components/Tooltip';
import HoverTooltip from '@Components/HoverTooltip';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';

function SubstanceDetailsContent({ structure, metadata }) {
  const databaseId = get(structure, 'databaseId');
  const molImage = get(structure, 'molImage');
  const currentDatabase = UserDatabases.getDatabaseById(databaseId) || {};
  const details = get(structure, 'details') || {};
  const resultName = get(metadata, 'resultName');

  function renderDbSpecificContent() {
    switch (databaseId) {
      case '1':
        return (
          <div className="substance-details">
            <ul className="substance-details-left">
              <li>
                <span className="rx-btn-text">
                  <span>Identification</span>
                </span>
              </li>
              <li>
                {get(structure, 'details.chemicalNameSynonyms.length') && (
                  <span className="rx-btn-text">
                    <span>More names</span>
                  </span>
                )}
              </li>
            </ul>
            <div className="substance-details-right">
              {map(get(structure, 'pubchemReferences'), (url, title) => (
                <span className="rx-btn-text" key={title}>
                  <InnerHTML tag="span" html={title} />
                </span>
              ))}
            </div>
          </div>
        );
      case '5':
      case '15':
      case '16':
        return (
          <div className="substance-details">
            <ul className="substance-details-left">
              <li>
                <span className="rx-btn-text">
                  <span>Identification</span>
                </span>
              </li>
              <li>
                <span className="rx-btn-text">
                  <span>Supplier Information</span>
                </span>
              </li>
            </ul>
            <div className="substance-details-right">
              <strong className="u-els-font-family-bold">Largest Quantity:</strong>
              <InnerHTML tag="div" html={get(structure, 'supplierData.largestQuantity')} />

              <strong className="u-els-font-family-bold">Average Price/g ($):</strong>
              <InnerHTML tag="div" html={get(structure, 'supplierData.avgPriceGram')} />

              <strong className="u-els-font-family-bold">Average Price/mmol ($):</strong>
              <InnerHTML tag="div" html={get(structure, 'supplierData.avgPriceMMol')} />
            </div>
          </div>
        );
      default: {
        const facts = get(structure, 'facts') || [];
        const links = get(structure, 'links') || {};

        return (
          <div className="substance-details">
            <ul className={classnames('substance-details-left', facts.length > 4 ? 'substance-details-left--columns' : null)}>
              {facts.map((fact, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>
                  <span>
                    {fact.title}&nbsp;{fact.count && <span>- {fact.count}</span>}
                  </span>
                </li>
              ))}
            </ul>
            <div className="substance-details-right">
              {!!links.preparationsCount && (
                <span className="rx-btn-text">
                  <span>Preparations - {links.preparationsCount}</span>
                </span>
              )}
              {!!links.reactionsCount && (
                <span className="rx-btn-text">
                  <span>Reactions - {links.reactionsCount}</span>
                </span>
              )}
              {!!links.targetsCount && (
                <span className="rx-btn-text">
                  <span>Targets - {links.targetsCount}</span>
                </span>
              )}
              {!!links.documentCount && (
                <span className="rx-btn-text">
                  <span>Documents - {links.documentCount}</span>
                </span>
              )}
            </div>
          </div>
        );
      }
    }
  }

  return (
    <div>
      <header className="u-els-margin-bottom-1x">
        <InnerHTML
          mode="escapeCustom"
          tag="h2"
          className="substance__title u-els-margin-bottom-1o2 u-els-font-size-base u-els-line-height-default u-els-font-family-bold"
          html={(details.chemicalNames && details.chemicalNames[0]) || `${currentDatabase.name} ID: ${details.id}`}
        />
        <div className="substance__meta u-els-display-flex u-els-font-size-meta">
          {/* formula */}
          {details.formula && (
            <HoverTooltip
              className="substance__meta__item"
              tag="span"
              theme="simple"
              tooltipPlace="bottom-start"
              id={TooltipConstants.ids.GlobalHover}
              tooltip="Linear Structure Formula">
              <span className="substance__meta__item__content">
                <InnerHTML tag="span" html={details.linearFormula} />
              </span>
            </HoverTooltip>
          )}
          {/* weight */}
          {details.weight && (
            <HoverTooltip
              className="substance__meta__item"
              tag="span"
              theme="simple"
              tooltipPlace="bottom-start"
              id={TooltipConstants.ids.GlobalHover}
              tooltip="Molecular Weight">
              <span className="substance__meta__item__content">
                <InnerHTML tag="span" html={details.weight} />
              </span>
            </HoverTooltip>
          )}
          {/* ID */}
          {details.id && (
            <HoverTooltip
              className="substance__meta__item"
              tag="span"
              theme="simple"
              tooltipPlace="bottom-start"
              id={TooltipConstants.ids.GlobalHover}
              tooltip={`${currentDatabase.name} ID`}>
              <span className="substance__meta__item__content">
                <InnerHTML tag="span" html={details.id} />
              </span>
            </HoverTooltip>
          )}
          {/* CAS */}
          {!Auth.isFeatureEnabled(FEATURES.disableCasNumberCta) ? (
            <CasNumberContainer
              className="substance__meta__item"
              casRegistryNumbers={details.casRegistryNumbers}
              substanceId={details.id}
              databaseId={databaseId}
              name={details.chemicalNames && details.chemicalNames[0]}
              structure={molImage && molImage.structure}
              inChiKey={details.inChiKey}
              withCta={false}
              resultName={resultName}
            />
          ) : (
            isCasNumberAllowed(databaseId) &&
            !isEmpty(details.casRegistryNumbers) && (
              <HoverTooltip
                className="substance__meta__item"
                tag="span"
                theme="simple"
                tooltipPlace="bottom-start"
                id={TooltipConstants.ids.GlobalHover}
                tooltip="CAS Number">
                <span className="substance__meta__item__content">
                  <InnerHTML
                    tag="span"
                    html={isArray(details.casRegistryNumbers) ? details.casRegistryNumbers?.join(', ') : details.casRegistryNumbers}
                  />
                </span>
              </HoverTooltip>
            )
          )}
        </div>
      </header>
      {/* Db-Specific Content */}
      {renderDbSpecificContent()}
    </div>
  );
}

SubstanceDetailsContent.propTypes = {
  structure: PropTypes.object.isRequired,
  metadata: MetadataPropTypesShape,
};

export default SubstanceDetailsContent;
